/**
 * ViolationStatusHistoryTab - Full audit trail of all status changes on a violation.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, CircularProgress, Alert, Tooltip,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import api from "../../utils/api";

const fmtDT = (d) => d ? new Date(d).toLocaleString("en-US", { month:"short", day:"numeric", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "N/A";

const ViolationStatusHistoryTab = ({ violationId }) => {
  const [history,  setHistory]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Pull from the timeline which includes status changes
      const res = await api.get(`/violationsTimeline/${violationId}`);
      const timeline = res.data || {};
      // Filter status-change events from the timeline
      const statusEvents = (timeline.timeline || []).filter(e =>
        e.type === "status_change" || e.event_type === "status_change" ||
        (e.old_status && e.new_status) || e.status_history
      );
      // Also try the direct status history if available
      const directRes = await api.get(`/violations/${violationId}/status-history`).catch(() => ({ data: [] }));
      const direct = directRes.data || [];
      // Merge, prefer direct endpoint if it has data
      setHistory(direct.length > 0 ? direct : statusEvents);
    } catch {
      setError("Failed to load status history.");
    } finally {
      setLoading(false);
    }
  }, [violationId]);

  useEffect(() => { load(); }, [load]);

  const statusColor = (s) => {
    if (!s) return { bg:"rgba(0,0,0,0.06)", color:"#6b778c" };
    const sl = s.toLowerCase();
    if (sl.includes("closed") || sl.includes("resolved")) return { bg:"rgba(22,163,74,0.1)",   color:"#16a34a" };
    if (sl.includes("open"))   return { bg:"rgba(47,99,173,0.1)",   color:"#2f63ad" };
    if (sl.includes("pending"))return { bg:"rgba(180,83,9,0.1)",    color:"#b45309" };
    if (sl.includes("fine") || sl.includes("escalat")) return { bg:"rgba(220,38,38,0.1)", color:"#dc2626" };
    return { bg:"rgba(124,58,237,0.1)", color:"#7c3aed" };
  };

  return (
    <Box>
      <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:2.5 }}>
        <HistoryIcon sx={{ color:"#d97706" }} />
        <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142" }}>Status Change History</Typography>
        <Chip size="small" label={`${history.length} changes`}
          sx={{ ml:1, fontWeight:700, fontSize:11, borderRadius:"8px", bgcolor:"rgba(47,99,173,0.08)", color:"#2f63ad" }} />
      </Box>

      {error && <Alert severity="error" sx={{ mb:2, borderRadius:2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display:"flex", justifyContent:"center", py:6 }}><CircularProgress /></Box>
      ) : history.length === 0 ? (
        <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", p:4, textAlign:"center" }}>
          <HistoryIcon sx={{ fontSize:40, color:"rgba(47,99,173,0.2)", mb:1 }} />
          <Typography variant="body2" sx={{ color:"#6b778c" }}>No status changes recorded yet.</Typography>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
          <Table size="small">
            <TableHead sx={{ bgcolor:"rgba(217,119,6,0.04)" }}>
              <TableRow>
                {["Date & Time","Changed By","From Status","To Status","Notes"].map(h => (
                  <TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((h, i) => {
                const fromStatus = h.old_status || h.from_status || h.previous_status || "-";
                const toStatus   = h.new_status || h.to_status   || h.status         || "-";
                const changedBy  = h.changed_by_name || h.changed_by || h.user_name  || "System";
                const changedAt  = h.changed_at || h.created_at || h.timestamp;
                const notes      = h.notes || h.comment || h.reason || "";
                const fromC = statusColor(fromStatus !== "-" ? fromStatus : null);
                const toC   = statusColor(toStatus   !== "-" ? toStatus   : null);
                return (
                  <TableRow key={i} hover sx={{ "&:last-child td":{ border:0 } }}>
                    <TableCell sx={{ fontSize:12, color:"#6b778c", whiteSpace:"nowrap" }}>{fmtDT(changedAt)}</TableCell>
                    <TableCell sx={{ fontWeight:600, color:"#223142", fontSize:12 }}>{changedBy}</TableCell>
                    <TableCell>
                      {fromStatus !== "-" ? (
                        <Chip size="small" label={fromStatus}
                          sx={{ fontWeight:600, fontSize:11, borderRadius:"8px", bgcolor:fromC.bg, color:fromC.color }} />
                      ) : <Typography variant="caption" sx={{ color:"#9aa5b4" }}>-</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={toStatus}
                        sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", bgcolor:toC.bg, color:toC.color }} />
                    </TableCell>
                    <TableCell sx={{ fontSize:11, color:"#6b778c", maxWidth:200 }}>
                      {notes ? (
                        <Tooltip title={notes}><span style={{ cursor:"help" }}>{notes.length > 60 ? notes.slice(0,60)+"…" : notes}</span></Tooltip>
                      ) : "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default ViolationStatusHistoryTab;
